import type { AxiosRequestConfig } from 'axios';

export const PRODUCT_CONTEXT_HEADER = 'X-Bustly-Product';
export const BUSTLY_CORE_PRODUCT_CODE = 'BUSTLY_CORE';
export const PLUGIN_PRODUCT_CODE = 'PLUGIN';

export type ProductCode = typeof BUSTLY_CORE_PRODUCT_CODE | typeof PLUGIN_PRODUCT_CODE;

const PLUGIN_HOST_SUFFIX = 'bizsidekick.app';

const LOCAL_DEVELOPMENT_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

const normalizeProductCode = (value?: string | null): ProductCode | null => {
  const normalizedValue = (value || '').trim().toUpperCase();
  if (normalizedValue === PLUGIN_PRODUCT_CODE) {
    return PLUGIN_PRODUCT_CODE;
  }
  if (normalizedValue === BUSTLY_CORE_PRODUCT_CODE) {
    return BUSTLY_CORE_PRODUCT_CODE;
  }
  return null;
};

const getConfiguredProductCode = (): ProductCode | null => {
  const nodeEnv = (globalThis as typeof globalThis & {
    process?: { env?: Record<string, string | undefined> };
  }).process?.env;
  return normalizeProductCode(process.env.VITE_PRODUCT_CODE || nodeEnv?.VITE_PRODUCT_CODE);
};

export const getProductCodeFromHost = (host?: string | null): ProductCode => {
  const normalizedHost = (host || '').trim().toLowerCase().split(':')[0];
  if (
    normalizedHost === PLUGIN_HOST_SUFFIX ||
    normalizedHost.endsWith(`.${PLUGIN_HOST_SUFFIX}`)
  ) {
    return PLUGIN_PRODUCT_CODE;
  }
  return BUSTLY_CORE_PRODUCT_CODE;
};

const isLocalDevelopmentHost = (host?: string | null): boolean => {
  const normalizedHost = (host || '').trim().toLowerCase().split(':')[0];
  return LOCAL_DEVELOPMENT_HOSTS.has(normalizedHost);
};

export const getCurrentProductCode = (): ProductCode => {
  if (typeof window !== 'undefined') {
    const host = window.location?.host;
    if (!isLocalDevelopmentHost(host)) {
      return getProductCodeFromHost(host);
    }
    return getConfiguredProductCode() || getProductCodeFromHost(host);
  }
  return getConfiguredProductCode() || BUSTLY_CORE_PRODUCT_CODE;
};

export const isPluginProduct = (productCode?: string | null): boolean => (
  productCode === PLUGIN_PRODUCT_CODE
);

export const getProductScopedStorageKey = (
  key: string,
  productCode: ProductCode,
): string => `${key}:${productCode}`;

export const withProductHeaders = (
  productCode: ProductCode,
  axiosRequestConfig?: AxiosRequestConfig,
): AxiosRequestConfig => ({
  ...(axiosRequestConfig || {}),
  headers: {
    ...(axiosRequestConfig?.headers || {}),
    [PRODUCT_CONTEXT_HEADER]: productCode,
  },
});

export const withPluginProductHeaders = (
  axiosRequestConfig?: AxiosRequestConfig,
): AxiosRequestConfig => withProductHeaders(PLUGIN_PRODUCT_CODE, axiosRequestConfig);
